import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  writeBatch,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ProcessedStockLabelsDialog from "./ProcessedStockLabelsDialog";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { isProcessedPackageActive, methodFromLegacy, PACKAGE_TYPES, processedPackageLabelWithParent, PRODUCT_FORMS, PROCESSED_PACKAGE_COLLECTION, ProcessMethod, ProcessedPackage } from "./processedCatalog";
import { availableProcessedPackages, isPackagingLanguage, matchesProcessedSource, PACKAGING_LANGUAGES, packagingLanguageLabel, PROCESSED_PUBLIC_STOCK_COLLECTION, publicProcessedStockTrace, sourceRefsOf, validTraceDates, type PackagingLanguage } from "./processedStockModel";
import { mainImageForLanguage } from "./processedProductMedia";
import { ShopApiError, shopAdminApi, shopStockApiConfigured, type ShopStockDraft } from "./webshop/shopAdminApi";
import { useConfirmedShopMutation } from "./webshop/useConfirmedShopMutation";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./freshStock.css";
import "./processedStock.css";

interface ProcessedParent {
  firebaseId: string;
  id: string;
  name_sv: string;
  name_en: string;
  icon?: string;
  mainImage?: string;
  linkedParents: string[];
  processMethod: ProcessMethod;
  isPublic: boolean;
}

interface FreezeDrySourceBatch {
  firebaseId: string;
  productId: string;
  product: string;
  batchId: string;
  process?: ProcessMethod;
  sourceType?: "grown_by_us" | "external";
  externalProductName?: string | null;
  freezeDryStart: number | null;
  freezeDryEnd: number | null;
  trayCount: number | null;
  freshWeight: number | null;
  dryWeight: number | null;
  createdAt: number;
}

interface ProcessedStockEntry {
  firebaseId: string;
  packageId?: string;
  productId?: string;
  sourceBatchRef?: string;
  sourceBatchId?: string;
  sourceProductId?: string;
  sourceBatchRefs?: string[];
  sourceBatchIds?: string[];
  sourceProductIds?: string[];
  batchId: string;
  processDate?: string;
  processedBy?: string;
  packingDate?: string;
  packedBy?: string;
  bestBeforeDate?: string;
  origin_sv?: string;
  origin_en?: string;
  packagingLanguage?: PackagingLanguage;
  storageLocation?: string;
  quantity?: number;
  reservedQuantity?: number;
  revision?: number;
  shopSaleStatus?: "approved" | "hold" | "recalled";
  boxPackageId?: string;
  boxQuantity?: number;
  boxMultiplier?: number;
  notes?: string;
  createdAt: number;
}

interface MultiStockRow {
  id: string;
  operationKey: string;
  product: ProcessedParent | null;
  sourceBatch: FreezeDrySourceBatch | null;
  package: ProcessedPackage | null;
  quantity: number;
  trace: StockTraceForm;
}

interface StockTraceForm {
  processDate: string;
  processedBy: string;
  packingDate: string;
  packedBy: string;
  bestBeforeDate: string;
  origin_sv: string;
  origin_en: string;
  packagingLanguage: PackagingLanguage | "";
  storageLocation: string;
  notes: string;
}

type StockFilter = "all" | "week" | "available" | "reserved" | "unavailable";
const STOCK_FILTERS: Array<{ value: StockFilter; label: string }> = [
  { value: "all", label: "Alla" }, { value: "week", label: "Packat i veckan" },
  { value: "available", label: "Ledigt" }, { value: "reserved", label: "Reserverat" },
  { value: "unavailable", label: "Inget ledigt" },
];
const stockCountLabel = (count: number) => `${count} ${count === 1 ? "lagerpost" : "lagerposter"}`;
const quantityOf = (entry: ProcessedStockEntry) => entry.quantity ?? 0;
const reservedOf = (entry: ProcessedStockEntry) => entry.reservedQuantity ?? 0;
const availableOf = (entry: ProcessedStockEntry) => Math.max(0, quantityOf(entry) - reservedOf(entry));
const PROCESSED_STOCK_COLLECTION = "products_freezedry_batch";
const emptyTrace = (): StockTraceForm => ({
  processDate: "", processedBy: "", packingDate: "", packedBy: "",
  bestBeforeDate: "", origin_sv: "", origin_en: "", packagingLanguage: "sv", storageLocation: "", notes: "",
});
const traceFromStock = (entry: ProcessedStockEntry): StockTraceForm => ({
  processDate: entry.processDate || "", processedBy: entry.processedBy || "",
  packingDate: entry.packingDate || "", packedBy: entry.packedBy || "",
  bestBeforeDate: entry.bestBeforeDate || "", storageLocation: entry.storageLocation || "",
  origin_sv: entry.origin_sv || "", origin_en: entry.origin_en || "",
  packagingLanguage: isPackagingLanguage(entry.packagingLanguage) ? entry.packagingLanguage : "",
  notes: entry.notes || "",
});
const traceReady = (trace: StockTraceForm) =>
  isPackagingLanguage(trace.packagingLanguage) && !!trace.processedBy.trim() && !!trace.packedBy.trim() &&
  validTraceDates(trace.processDate, trace.packingDate, trace.bestBeforeDate);
const stockApiError = (error: unknown, generic: string) => error instanceof ShopApiError && error.status === 409
  ? "Lagerposten har ändrats eller skyddas av reservation, orderhistorik eller återkallelse. Läs in aktuell version; historiska kopplingar och spårningsuppgifter måste bevaras."
  : generic;
const stockDraft = (data: ShopStockDraft): ShopStockDraft => ({
  productId: data.productId, packageId: data.packageId, sourceBatchRefs: data.sourceBatchRefs,
  processDate: data.processDate, processedBy: data.processedBy,
  packingDate: data.packingDate, packedBy: data.packedBy,
  bestBeforeDate: data.bestBeforeDate, storageLocation: data.storageLocation,
  origin_sv: data.origin_sv, origin_en: data.origin_en,
  packagingLanguage: data.packagingLanguage,
  notes: data.notes, quantity: data.quantity,
});

function StockDialog({ id, open, title, description, onClose, busy, error, wide, children, actions }: {
  id: string; open: boolean; title: string; description: string; onClose: () => void;
  busy?: boolean; error?: string; wide?: boolean; children: React.ReactNode; actions: React.ReactNode;
}) {
  return <Dialog className="FreshDialog BatchDialog StockDialog" fullWidth maxWidth={wide ? "md" : "sm"}
    open={open} onClose={busy ? undefined : onClose} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
    <DialogTitle component="div"><div><h2 id={`${id}-title`}>{title}</h2><p className="BatchDialogSubtitle" id={`${id}-description`}>{description}</p></div><IconButton aria-label="Stäng" onClick={onClose} disabled={busy}><CloseIcon /></IconButton></DialogTitle>
    <DialogContent>{children}</DialogContent>
    {error && <Alert severity="error" className="BatchSaveError">{error}</Alert>}
    <DialogActions>{actions}</DialogActions>
  </Dialog>;
}

function StockFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <fieldset className="BatchFormSection"><legend>{title}</legend>{children}</fieldset>;
}

function StockTraceFields({ value, onChange, busy }: {
  value: StockTraceForm; onChange: (changes: Partial<StockTraceForm>) => void; busy: boolean;
}) {
  return <div className="StockFormGrid ProcessedStockTraceFields">
    <TextField label="Processdatum *" type="date" size="small" fullWidth value={value.processDate}
      onChange={(event) => onChange({ processDate: event.target.value })} disabled={busy}
      InputLabelProps={{ shrink: true }} helperText="Datum då produkten processades." />
    <TextField label="Processad av *" size="small" fullWidth value={value.processedBy}
      onChange={(event) => onChange({ processedBy: event.target.value })} disabled={busy}
      helperText="Visningsnamn för publik spårning när produkten är publik." />
    <TextField label="Packdatum *" type="date" size="small" fullWidth value={value.packingDate}
      onChange={(event) => onChange({ packingDate: event.target.value })} disabled={busy}
      InputLabelProps={{ shrink: true }} helperText="Används för kundens datumkod." />
    <TextField label="Packad av *" size="small" fullWidth value={value.packedBy}
      onChange={(event) => onChange({ packedBy: event.target.value })} disabled={busy}
      helperText="Visningsnamn för publik spårning när produkten är publik." />
    <TextField label="Lagerplats" size="small" fullWidth value={value.storageLocation}
      onChange={(event) => onChange({ storageLocation: event.target.value })} disabled={busy}
      helperText="Valfritt, endast internt." />
    <TextField className="ProcessedStockTraceWide" label="Interna anteckningar" size="small" fullWidth
      multiline minRows={2} value={value.notes} onChange={(event) => onChange({ notes: event.target.value })}
      disabled={busy} helperText="Till exempel kvalitetskontroll eller avvikelse. Publiceras aldrig." />
  </div>;
}

function StockLabelFields({ value, onChange, busy }: {
  value: StockTraceForm; onChange: (changes: Partial<StockTraceForm>) => void; busy: boolean;
}) {
  return <StockFormSection title="Batch-etikett">
    <p className="StockHelpText ProcessedStockLabelHint">Uppgifterna gäller endast den här lagerposten och används även för etiketter till kopplade boxar. Etikettens mått och färger anges i Processade förpackningar.</p>
    <TextField select label="Förpackningsspråk" required size="small" fullWidth value={value.packagingLanguage} disabled={busy}
      onChange={(event) => onChange({ packagingLanguage: event.target.value as PackagingLanguage })}
      error={!isPackagingLanguage(value.packagingLanguage)} helperText="Alla paket i lagerposten ska ha samma språk. Registrera svenska och engelska förpackningar som separata lagerposter, även med samma EAN. Batch-etiketter får automatiskt detta språk.">
      <MenuItem value="" disabled>Välj språk</MenuItem>
      {PACKAGING_LANGUAGES.map((language) => <MenuItem key={language.value} value={language.value}>{language.label}</MenuItem>)}
    </TextField>
    <div className="StockFormGrid">
      <TextField label="Ursprung · svenska" size="small" fullWidth value={value.origin_sv} disabled={busy}
        onChange={(event) => onChange({ origin_sv: event.target.value })} inputProps={{ maxLength: 120 }}
        helperText="Hela ursprungstexten för den här batchen. Krävs för svenska etiketter." />
      <TextField label="Ursprung · engelska" size="small" fullWidth value={value.origin_en} disabled={busy}
        onChange={(event) => onChange({ origin_en: event.target.value })} inputProps={{ maxLength: 120 }}
        helperText="Separat ursprungstext. Krävs för engelska etiketter." />
      <TextField label="Batchnummer / datumkod" size="small" fullWidth
        value={value.packingDate ? value.packingDate.slice(2).replace(/-/g, "") : ""}
        disabled={busy} InputProps={{ readOnly: true }} helperText="Beräknas från den här lagerpostens packdatum (ÅÅMMDD)." />
      <TextField label="Bäst före" type="date" size="small" fullWidth value={value.bestBeforeDate}
        onChange={(event) => onChange({ bestBeforeDate: event.target.value })} disabled={busy}
        InputLabelProps={{ shrink: true }} helperText="Gäller den här batchen. Krävs för att skriva ut batch-etiketter." />
    </div>
  </StockFormSection>;
}

function DashBoardFreezeDryStock() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  // --- List state ---
  const [stockEntries, setStockEntries] = useState<ProcessedStockEntry[]>([]);
  const [packages, setPackages] = useState<ProcessedPackage[]>([]);
  const [parents, setParents] = useState<ProcessedParent[]>([]);
  const [sourceBatches, setSourceBatches] = useState<FreezeDrySourceBatch[]>([]);
  const [loadingCollections, setLoadingCollections] = useState({ products: true, packages: true, batches: true, stock: true });
  const firebaseLoading = Object.values(loadingCollections).some(Boolean);
  const [loadErrors, setLoadErrors] = useState<Partial<Record<keyof typeof loadingCollections, string>>>({});
  const [reload, setReload] = useState(0);
  const [sortBy, setSortBy] = useState<"package" | "batch" | "created">("created");
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [languageFilter, setLanguageFilter] = useState<"all" | PackagingLanguage | "unset">("all");
  const [singleError, setSingleError] = useState("");
  const [multiError, setMultiError] = useState("");
  const [deletingEntry, setDeletingEntry] = useState<ProcessedStockEntry | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  // --- Info dialog ---
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);

  // --- Single batch edit dialog ---
  const [open, setOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ProcessedStockEntry | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [singleReason, setSingleReason] = useState("");

  // --- Multi-batch add dialog ---
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiDialogLoading, setMultiDialogLoading] = useState(false);

  const singleMutation = useConfirmedShopMutation(() => { clearFilters(); setOpen(false); }, user?.uid);
  const deleteMutation = useConfirmedShopMutation(() => setDeleteOpen(false), user?.uid);
  const multiMutation = useConfirmedShopMutation(() => { clearFilters(); setMultiOpen(false); }, user?.uid);
  const singleBusy = dialogLoading || singleMutation.saving;
  const singleLocked = singleBusy || singleMutation.pending || singleMutation.conflict;
  const deleteBusy = deleteLoading || deleteMutation.saving;
  const deleteLocked = deleteBusy || deleteMutation.pending || deleteMutation.conflict;
  const multiBusy = multiDialogLoading || multiMutation.saving;
  const multiLocked = multiBusy || multiMutation.pending || multiMutation.conflict;

  // Form fields for single add/edit
  const [singleProduct, setSingleProduct] = useState<ProcessedParent | null>(null);
  const [singlePackage, setSinglePackage] = useState<ProcessedPackage | null>(null);
  const [singleSourceBatches, setSingleSourceBatches] = useState<FreezeDrySourceBatch[]>([]);
  const [singleQuantity, setSingleQuantity] = useState(1);
  const [singleTrace, setSingleTrace] = useState<StockTraceForm>(emptyTrace);
  const [stockTemplateId, setStockTemplateId] = useState("");

  // Form fields for multi-lager add
  const [multiRows, setMultiRows] = useState<MultiStockRow[]>([]);

  // Product IDs here are the processed product IDs used by package documents.
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_freezedry"), orderBy("name_sv"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: ProcessedParent[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({
          firebaseId: doc.id,
          id: data.id || doc.id,
          name_sv: data.name_sv || "",
          name_en: data.name_en || "",
          icon: data.icon || "",
          mainImage: mainImageForLanguage(data, "sv"),
          linkedParents: Array.isArray(data.linkedParents) ? data.linkedParents : [],
          processMethod: methodFromLegacy(data),
          isPublic: data.isPublic === true,
        });
      });
      setParents(temp);
      setLoadingCollections((prev) => ({ ...prev, products: false }));
    }, (error) => {
      console.error("Error loading products:", error);
      setLoadErrors((prev) => ({ ...prev, products: "Det gick inte att hämta produkter." }));
      setLoadingCollections((prev) => ({ ...prev, products: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Listen to packages
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(firebaseStore, PROCESSED_PACKAGE_COLLECTION), (snapshot) => {
      const temp: ProcessedPackage[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({ ...data, firebaseId: doc.id, id: data.id || doc.id,
          packageType: data.packageType || (data.isBox ? "box" : "bag"), productForm: data.productForm || "whole",
          active: data.active !== false, boundPackageId: data.boundPackageId || "" } as ProcessedPackage);
      });
      setPackages(temp);
      setLoadingCollections((prev) => ({ ...prev, packages: false }));
    }, (error) => {
      console.error("Error loading packages:", error);
      setLoadErrors((prev) => ({ ...prev, packages: "Det gick inte att hämta förpackningar." }));
      setLoadingCollections((prev) => ({ ...prev, packages: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Listen to freeze-dried and dehydrated source batches.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(firebaseStore, "freezedry_batches"), (snapshot) => {
      const temp: FreezeDrySourceBatch[] = [];
      snapshot.forEach((doc) => { temp.push({ ...doc.data(), firebaseId: doc.id } as FreezeDrySourceBatch); });
      setSourceBatches(temp.sort((a, b) => (b.freezeDryEnd || b.freezeDryStart || b.createdAt || 0) - (a.freezeDryEnd || a.freezeDryStart || a.createdAt || 0)));
      setLoadingCollections((prev) => ({ ...prev, batches: false }));
    }, (error) => {
      console.error("Error loading batches:", error);
      setLoadErrors((prev) => ({ ...prev, batches: "Det gick inte att hämta processbatcher." }));
      setLoadingCollections((prev) => ({ ...prev, batches: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Legacy product-level records have no packageId; they stay visible until linked during edit.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(firebaseStore, PROCESSED_STOCK_COLLECTION), (snapshot) => {
      const temp: ProcessedStockEntry[] = [];
      snapshot.forEach((doc) => { temp.push({ ...doc.data(), firebaseId: doc.id } as ProcessedStockEntry); });
      setStockEntries(temp);
      setLoadingCollections((prev) => ({ ...prev, stock: false }));
    }, (error) => {
      console.error("Error loading stock:", error);
      setLoadErrors((prev) => ({ ...prev, stock: "Det gick inte att hämta lagerposter." }));
      setLoadingCollections((prev) => ({ ...prev, stock: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => { if (user === null && loading === false) navigate("/login"); }, [user, loading, navigate]);

  // --- Helpers ---

  const getProductById = (productId: string) => parents.find((parent) => parent.id === productId);
  const getPackageById = (packageId?: string) => packages.find((pkg) => pkg.id === packageId);
  const getEntryProductId = (entry: ProcessedStockEntry) => getPackageById(entry.packageId)?.parentId || entry.productId || "";
  const getPackageLabel = (packageId?: string, legacyProductId?: string): string => {
    const pkg = getPackageById(packageId);
    const parent = getProductById(pkg?.parentId || legacyProductId || "");
    if (!pkg) return (parent?.name_sv || "Okänd produkt") + " — förpackning saknas";
    const form = PRODUCT_FORMS.find((option) => option.value === pkg.productForm)?.label || pkg.productForm;
    const type = PACKAGE_TYPES.find((option) => option.value === pkg.packageType)?.label || pkg.packageType;
    return (parent?.name_sv || pkg.parentId) + " — " + processedPackageLabelWithParent(pkg, packages) + " · " + type + ", " + form;
  };
  const getPackageOptionLabel = (pkg: ProcessedPackage): string => {
    const form = PRODUCT_FORMS.find((option) => option.value === pkg.productForm)?.label || pkg.productForm;
    const type = PACKAGE_TYPES.find((option) => option.value === pkg.packageType)?.label || pkg.packageType;
    return processedPackageLabelWithParent(pkg, packages) + " · " + type + ", " + form;
  };
  const getSourceBatchById = (ref?: string) => sourceBatches.find((batch) => batch.firebaseId === ref);
  const getEntrySources = (entry: ProcessedStockEntry) => sourceRefsOf(entry)
    .map((ref) => getSourceBatchById(ref)).filter((batch): batch is FreezeDrySourceBatch => !!batch);
  const getSourceProductName = (batch: FreezeDrySourceBatch) => batch.externalProductName || batch.product || batch.productId || "Okänd råvara";
  const isSourceBatchAllowed = (productId: string, batch: FreezeDrySourceBatch): boolean => {
    const product = getProductById(productId);
    return !!product && matchesProcessedSource(product, batch);
  };
  const getSourceBatchesForProduct = (productId: string, includeBatchRefs: string[] = []) => sourceBatches.filter((batch) =>
    includeBatchRefs.includes(batch.firebaseId) ||
    (isSourceBatchAllowed(productId, batch) && !!batch.freezeDryEnd && batch.freezeDryEnd <= Date.now())
  );
  const isCompletedOrExistingBatch = (batch: FreezeDrySourceBatch, editingEntry?: ProcessedStockEntry | null) =>
    (!!editingEntry && sourceRefsOf(editingEntry).includes(batch.firebaseId)) || (!!batch.freezeDryEnd && batch.freezeDryEnd <= Date.now());
  const getAvailablePackages = (
    productId: string, sourceBatchRef: string, editingEntryId?: string, excludedPackageIds: string[] = [],
  ): ProcessedPackage[] => availableProcessedPackages(packages, stockEntries, productId, sourceBatchRef, editingEntryId, excludedPackageIds);
  const getCommonAvailablePackages = (productId: string, batches: FreezeDrySourceBatch[], editingEntryId?: string) => {
    if (!batches.length) return [];
    return packages.filter((pkg) => pkg.parentId === productId && pkg.packageType !== "box" &&
      batches.every((batch) => getAvailablePackages(productId, batch.firebaseId, editingEntryId).some((available) => available.id === pkg.id)));
  };
  const getBoxPackageInfo = (basePackage: ProcessedPackage): { boxPackage: ProcessedPackage; multiplier: number } | null => {
    const matchingBox = packages.filter((pkg) => pkg.packageType === "box" && pkg.parentId === basePackage.parentId &&
      pkg.boundPackageId === basePackage.id && pkg.unitsPerBox > 1 && isProcessedPackageActive(pkg, packages))
      .sort((a, b) => a.unitsPerBox - b.unitsPerBox)[0];
    return matchingBox ? { boxPackage: matchingBox, multiplier: matchingBox.unitsPerBox } : null;
  };
  const getAutomaticBoxData = (basePackage: ProcessedPackage, quantity: number) => {
    const boxInfo = getBoxPackageInfo(basePackage);
    return boxInfo
      ? { boxPackageId: boxInfo.boxPackage.id, boxQuantity: Math.floor(quantity / boxInfo.multiplier), boxMultiplier: boxInfo.multiplier }
      : { boxPackageId: "", boxQuantity: 0, boxMultiplier: 0 };
  };
  const getSortedEntries = (): ProcessedStockEntry[] => {
    const sorted = [...stockEntries];
    if (sortBy === "package") {
      sorted.sort((a, b) => getPackageLabel(a.packageId, a.productId).localeCompare(getPackageLabel(b.packageId, b.productId), "sv-SE"));
    } else if (sortBy === "batch") {
      sorted.sort((a, b) => {
        const dateA = a.packingDate || "";
        const dateB = b.packingDate || "";
        return dateB.localeCompare(dateA) || (b.createdAt || 0) - (a.createdAt || 0);
      });
    } else {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  };
  const isThisWeek = (entry: ProcessedStockEntry) => {
    const packed = entry.packingDate;
    return !!packed && packed >= moment().startOf("isoWeek").format("YYYY-MM-DD") &&
      packed <= moment().endOf("isoWeek").format("YYYY-MM-DD");
  };
  const matchesFilter = (entry: ProcessedStockEntry, filter: StockFilter) => {
    if (filter === "week") return isThisWeek(entry);
    if (filter === "available") return availableOf(entry) > 0;
    if (filter === "reserved") return reservedOf(entry) > 0;
    if (filter === "unavailable") return availableOf(entry) === 0;
    return true;
  };
  const searchTerm = search.trim().toLocaleLowerCase("sv-SE");
  const matchesSearch = (entry: ProcessedStockEntry) => {
    const sources = getEntrySources(entry);
    const pkg = getPackageById(entry.packageId);
    return [getPackageLabel(entry.packageId, entry.productId), entry.batchId, entry.sourceBatchId,
      ...(entry.sourceBatchIds || []), ...sources.flatMap((source) => [source.batchId, getSourceProductName(source)]),
      entry.processDate, entry.packingDate, entry.bestBeforeDate, entry.processedBy, entry.packedBy,
      entry.storageLocation, pkg?.ean, entry.notes, packagingLanguageLabel(entry.packagingLanguage),
    ].some((value) => value?.toLocaleLowerCase("sv-SE").includes(searchTerm));
  };
  const clearFilters = () => { setSearch(""); setStockFilter("all"); setLanguageFilter("all"); };
  const upsertStock = (entry: ProcessedStockEntry) => setStockEntries((items) => [
    { ...items.find((item) => item.firebaseId === entry.firebaseId), ...entry },
    ...items.filter((item) => item.firebaseId !== entry.firebaseId),
  ]);
  const sourceBatchLabel = (batch: FreezeDrySourceBatch) => batch.batchId + " · " + getSourceProductName(batch);

  // --- Single edit helpers ---

  const resolveStockTemplate = (entry: ProcessedStockEntry) => {
    const matchingPackages = packages.filter((pkg) => pkg.id === entry.packageId);
    if (entry.shopSaleStatus === "recalled" || matchingPackages.length !== 1) return null;
    const pkg = matchingPackages[0];
    const matchingProducts = parents.filter((product) => product.id === pkg.parentId);
    if (matchingProducts.length !== 1 || (entry.productId && entry.productId !== pkg.parentId)) return null;
    const product = matchingProducts[0];
    const sources = getEntrySources(entry);
    if (!sources.length || sources.length !== sourceRefsOf(entry).length ||
      sources.some((source) => !isSourceBatchAllowed(product.id, source) || !Number.isFinite(source.freezeDryEnd) ||
        source.freezeDryEnd! <= 0 || !isCompletedOrExistingBatch(source)) ||
      !getCommonAvailablePackages(product.id, sources).some((available) => available.firebaseId === pkg.firebaseId)) return null;
    return { product, pkg, sources };
  };
  const stockTemplates = getSortedEntries().filter((entry) => resolveStockTemplate(entry) !== null);
  const selectedStockTemplate = stockTemplates.find((entry) => entry.firebaseId === stockTemplateId) || null;
  const stockTemplateLabel = (entry: ProcessedStockEntry) =>
    `${getPackageLabel(entry.packageId, entry.productId)} · ${entry.batchId} · ${packagingLanguageLabel(entry.packagingLanguage)}`;

  const resetSingleForm = () => {
    setSingleError("");
    setSingleReason("");
    singleMutation.reset();
    setSingleProduct(null);
    setSinglePackage(null);
    setSingleSourceBatches([]);
    setSingleQuantity(1);
    setSingleTrace(emptyTrace());
    setStockTemplateId("");
    setEditingEntry(null);
  };

  const handleOpenAdd = () => { if (singleMutation.pending || singleBusy) return; resetSingleForm(); setOpen(true); };

  const applyStockTemplate = (entry: ProcessedStockEntry | null) => {
    if (singleLocked || editingEntry) return;
    if (!entry) { setStockTemplateId(""); return; }
    const template = resolveStockTemplate(entry);
    if (!template) { setSingleError("Lagerposten kan inte längre användas som mall. Kontrollera produkt, förpackning och processbatcher."); return; }
    setSingleError("");
    setStockTemplateId(entry.firebaseId);
    setSingleProduct(template.product);
    setSinglePackage(template.pkg);
    setSingleSourceBatches(template.sources);
    setSingleTrace(traceFromStock(entry));
    // A new physical lot needs its own count; never duplicate quantities or reservations.
    setSingleQuantity(0);
  };

  const handleOpenEdit = (entry: ProcessedStockEntry) => {
    if (singleMutation.pending || singleBusy) return;
    setSingleError("");
    setSingleReason("");
    singleMutation.reset();
    setEditingEntry(entry);
    setStockTemplateId("");
    const pkg = getPackageById(entry.packageId);
    const product = getProductById(pkg?.parentId || entry.productId || "");
    setSingleProduct(product || null);
    setSinglePackage(pkg || null);
    setSingleSourceBatches(getEntrySources(entry));
    setSingleQuantity(Math.max(1, quantityOf(entry)));
    setSingleTrace(traceFromStock(entry));
    setOpen(true);
  };

  const handleClose = () => { if (!singleBusy && !singleMutation.pending) setOpen(false); };

  const handleDeleteEntry = async () => {
    if (deleteMutation.pending) return deleteMutation.retry();
    if (deleteBusy || deleteMutation.conflict) return;
    if (!deletingEntry) return;
    const current = stockEntries.find((entry) => entry.firebaseId === deletingEntry.firebaseId) || deletingEntry;
    if (reservedOf(current) > 0) {
      setDeleteError("Posten har reserverade paket. Frigör reservationerna innan lagerposten tas bort.");
      return;
    }
    if (shopStockApiConfigured() && !deleteReason.trim()) {
      setDeleteError("Ange varför lagerposten ska tas bort.");
      return;
    }
    setDeleteLoading(true);
    setDeleteError("");
    try {
      if (shopStockApiConfigured()) {
        if (!user) throw new Error("Not signed in");
        const input = { expectedRevision: deletingEntry.revision ?? 0, reason: deleteReason.trim() };
        await deleteMutation.submit(async (key) => {
          await shopAdminApi.deleteStock(user, current.firebaseId, input, key);
          setStockEntries((entries) => entries.filter((entry) => entry.firebaseId !== current.firebaseId));
        });
        return;
      } else {
        const batch = writeBatch(firebaseStore);
        batch.delete(doc(firebaseStore, PROCESSED_STOCK_COLLECTION, current.firebaseId));
        batch.delete(doc(firebaseStore, PROCESSED_PUBLIC_STOCK_COLLECTION, current.firebaseId));
        await batch.commit();
      }
      setStockEntries((entries) => entries.filter((entry) => entry.firebaseId !== current.firebaseId));
      setDeleteOpen(false);
    } catch (error) {
      console.error("Error deleting stock:", error);
      setDeleteError(stockApiError(error, "Lagerposten kunde inte tas bort. Kontrollera anslutningen och försök igen."));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveSingle = async () => {
    if (singleMutation.pending) return singleMutation.retry();
    if (singleBusy || singleMutation.conflict) return;
    if (!singleProduct) { setSingleError("Välj en processad produkt"); return; }
    if (!singleSourceBatches.length) { setSingleError("Välj minst en intern processbatch"); return; }
    if (!singlePackage) { setSingleError("Välj en förpackning"); return; }
    if (!Number.isInteger(singleQuantity) || singleQuantity < 1) {
      setSingleError("Ange ett totalt antal paket på minst 1");
      return;
    }
    if (!traceReady(singleTrace)) {
      setSingleError("Välj förpackningsspråk och ange processdatum, processad av, packdatum och packad av. Processdatum måste vara senast packdatum och bäst före får inte vara tidigare än packdatum.");
      return;
    }
    if (editingEntry && shopStockApiConfigured() && !singleReason.trim()) {
      setSingleError("Ange varför lagerposten ändras.");
      return;
    }
    const currentEntry = stockEntries.find((entry) => entry.firebaseId === editingEntry?.firebaseId) || editingEntry;
    if (currentEntry && singleQuantity < reservedOf(currentEntry)) {
      setSingleError(`Antalet kan inte vara mindre än reserverat antal (${reservedOf(currentEntry)})`);
      return;
    }
    const currentProduct = getProductById(singleProduct.id);
    if (!currentProduct) { setSingleError("Produkten finns inte längre i registret"); return; }
    const existingProductId = editingEntry && getEntryProductId(editingEntry);
    if (singlePackage.parentId !== currentProduct.id || singlePackage.packageType === "box" ||
      singleSourceBatches.some((sourceBatch) => !isSourceBatchAllowed(currentProduct.id, sourceBatch) &&
        !(existingProductId === currentProduct.id && sourceRefsOf(editingEntry!).includes(sourceBatch.firebaseId)))) {
      setSingleError("Produkt, interna batcher och förpackning måste höra ihop");
      return;
    }
    if (singleSourceBatches.some((sourceBatch) => !isCompletedOrExistingBatch(sourceBatch, editingEntry))) {
      setSingleError("Välj bara avslutade processbatcher");
      return;
    }
    const selectedRefs = singleSourceBatches.map((source) => source.firebaseId);
    if (currentEntry && reservedOf(currentEntry) > 0 &&
      ((!!getPackageById(currentEntry.packageId) && currentEntry.packageId !== singlePackage.id) ||
        sourceRefsOf(currentEntry).slice().sort().join("|") !== selectedRefs.slice().sort().join("|"))) {
      setSingleError("En lagerpost med reservationer kan inte flyttas till andra batcher eller en annan förpackning.");
      return;
    }
    if (selectedRefs.some((ref) =>
      !getAvailablePackages(currentProduct.id, ref, editingEntry?.firebaseId).some((pkg) => pkg.id === singlePackage.id))) {
      setSingleError("Förpackningen är inte valbar för minst en processbatch. Kontrollera äldre lagerposter och förpackningens status.");
      return;
    }

    setSingleError("");
    setDialogLoading(true);
    const createdAt = currentEntry?.createdAt || Date.now();
    const data = {
      productId: currentProduct.id,
      packageId: singlePackage.id,
      sourceBatchRef: selectedRefs[0],
      sourceBatchId: singleSourceBatches[0].batchId,
      sourceProductId: singleSourceBatches[0].productId,
      sourceBatchRefs: selectedRefs,
      sourceBatchIds: singleSourceBatches.map((source) => source.batchId),
      sourceProductIds: singleSourceBatches.map((source) => source.productId),
      batchId: singleTrace.packingDate.slice(2).replace(/-/g, ""),
      processDate: singleTrace.processDate,
      processedBy: singleTrace.processedBy.trim(),
      packingDate: singleTrace.packingDate,
      packedBy: singleTrace.packedBy.trim(),
      bestBeforeDate: singleTrace.bestBeforeDate,
      origin_sv: singleTrace.origin_sv.trim(), origin_en: singleTrace.origin_en.trim(),
      packagingLanguage: singleTrace.packagingLanguage as PackagingLanguage,
      storageLocation: singleTrace.storageLocation.trim(),
      notes: singleTrace.notes.trim(),
      quantity: singleQuantity,
      ...getAutomaticBoxData(singlePackage, singleQuantity),
      createdAt,
    };

    try {
      if (shopStockApiConfigured()) {
        if (!user) throw new Error("Not signed in");
        const draft = stockDraft(data);
        const payload = currentEntry ? { ...draft, expectedRevision: editingEntry?.revision ?? 0,
          reason: singleReason.trim() } : draft;
        await singleMutation.submit(async (key) => {
          const result = currentEntry
            ? await shopAdminApi.updateStock(user, currentEntry.firebaseId, payload as ShopStockDraft & { expectedRevision: number; reason: string }, key)
            : await shopAdminApi.createStock(user, draft, key);
          upsertStock({ ...currentEntry, ...data, firebaseId: result.stockDocId,
            reservedQuantity: result.reservedQuantity, revision: result.revision,
            shopSaleStatus: result.shopSaleStatus || currentEntry?.shopSaleStatus || "hold" });
        });
        return;
      } else {
        const privateRef = currentEntry
          ? doc(firebaseStore, PROCESSED_STOCK_COLLECTION, currentEntry.firebaseId)
          : doc(collection(firebaseStore, PROCESSED_STOCK_COLLECTION));
        const publicRef = doc(firebaseStore, PROCESSED_PUBLIC_STOCK_COLLECTION, privateRef.id);
        const publicData = publicProcessedStockTrace(data, currentProduct.firebaseId, currentProduct.isPublic);
        const batch = writeBatch(firebaseStore);
        if (currentEntry) batch.update(privateRef, data);
        else batch.set(privateRef, { ...data, reservedQuantity: 0 });
        if (publicData) batch.set(publicRef, publicData);
        else if (currentEntry) batch.delete(publicRef);
        await batch.commit();
        upsertStock({ ...currentEntry, ...data, firebaseId: privateRef.id });
      }
      clearFilters();
      setOpen(false);
    } catch (error) {
      console.error("Error saving processed stock:", error);
      setSingleError(stockApiError(error, "Lagerposten kunde inte sparas. Kontrollera API-anslutningen och behörigheten och försök igen."));
    } finally {
      setDialogLoading(false);
    }
  };

  // --- Multi-batch helpers ---

  const createMultiRow = (): MultiStockRow => ({
    id: `${Date.now()}-${Math.random()}`,
    operationKey: crypto.randomUUID(),
    product: null,
    sourceBatch: null,
    package: null,
    quantity: 1,
    trace: emptyTrace(),
  });

  const resetMultiForm = () => {
    multiMutation.reset();
    setMultiError("");
    setMultiRows([createMultiRow()]);
  };

  const handleOpenMultiAdd = () => { if (multiMutation.pending || multiBusy) return; resetMultiForm(); setMultiOpen(true); };

  const handleCloseMulti = () => { if (!multiBusy && !multiMutation.pending) setMultiOpen(false); };

  const updateMultiRow = (rowId: string, changes: Partial<MultiStockRow>) => {
    setMultiRows((rows) => rows.map((row) => (row.id === rowId ? { ...row, ...changes, operationKey: crypto.randomUUID() } : row)));
  };

  const getMultiRowPackages = (row: MultiStockRow): ProcessedPackage[] => {
    if (!row.product || !row.sourceBatch) return [];
    return getAvailablePackages(row.product.id, row.sourceBatch.firebaseId);
  };

  const handleSaveMulti = async () => {
    if (multiMutation.pending) return multiMutation.retry();
    if (multiBusy || multiMutation.conflict) return;
    if (!multiRows.length || multiRows.some((row) => !row.product || !row.sourceBatch || !row.package)) {
      setMultiError("Välj produkt, intern processbatch och förpackning för varje rad");
      return;
    }
    if (multiRows.some((row) => !Number.isInteger(row.quantity) || row.quantity < 1)) {
      setMultiError("Ange ett totalt antal på minst 1 för varje rad");
      return;
    }
    if (multiRows.some((row) => !traceReady(row.trace))) {
      setMultiError("Varje rad behöver förpackningsspråk, giltiga process- och packdatum samt namn på den som processade och packade.");
      return;
    }
    if (multiRows.some((row) => !isSourceBatchAllowed(row.product!.id, row.sourceBatch!) ||
      row.package!.parentId !== row.product!.id || row.package!.packageType === "box")) {
      setMultiError("Produkt, intern batch och förpackning måste höra ihop på varje rad");
      return;
    }
    if (multiRows.some((row) => !isCompletedOrExistingBatch(row.sourceBatch!))) {
      setMultiError("Alla processbatcher måste vara avslutade");
      return;
    }
    const rowKeys = multiRows.map((row) => `${row.sourceBatch!.firebaseId}:${row.package!.id}:${row.trace.packingDate}:${row.trace.packagingLanguage}`);
    if (new Set(rowKeys).size !== rowKeys.length) {
      setMultiError("Samma förpackning, processbatch, packdatum och förpackningsspråk finns på flera rader");
      return;
    }
    if (multiRows.some((row) => !getAvailablePackages(row.product!.id, row.sourceBatch!.firebaseId)
      .some((pkg) => pkg.id === row.package!.id))) {
      setMultiError("En eller flera förpackningar är inte valbara. Kontrollera äldre lagerposter och förpackningarnas status.");
      return;
    }

    setMultiError("");
    setMultiDialogLoading(true);
    try {
      const completed = new Set<string>();
      const saveRows = async () => {
      for (const row of multiRows) {
        if (completed.has(row.id)) continue;
        const product = getProductById(row.product!.id);
        if (!product) throw new Error("Processed product was removed before save");
        const source = row.sourceBatch!;
        const pkg = row.package!;
        const createdAt = Date.now();
        const data = {
          productId: product.id,
          packageId: pkg.id,
          sourceBatchRef: source.firebaseId,
          sourceBatchId: source.batchId,
          sourceProductId: source.productId,
          sourceBatchRefs: [source.firebaseId],
          sourceBatchIds: [source.batchId],
          sourceProductIds: [source.productId],
          batchId: row.trace.packingDate.slice(2).replace(/-/g, ""),
          processDate: row.trace.processDate,
          processedBy: row.trace.processedBy.trim(),
          packingDate: row.trace.packingDate,
          packedBy: row.trace.packedBy.trim(),
          bestBeforeDate: row.trace.bestBeforeDate,
          origin_sv: row.trace.origin_sv.trim(), origin_en: row.trace.origin_en.trim(),
          packagingLanguage: row.trace.packagingLanguage as PackagingLanguage,
          storageLocation: row.trace.storageLocation.trim(),
          notes: row.trace.notes.trim(),
          quantity: row.quantity,
          ...getAutomaticBoxData(pkg, row.quantity),
          createdAt,
        };
        if (shopStockApiConfigured()) {
          if (!user) throw new Error("Not signed in");
          const result = await shopAdminApi.createStock(user, stockDraft(data), row.operationKey);
          upsertStock({ ...data, firebaseId: result.stockDocId,
            reservedQuantity: result.reservedQuantity, revision: result.revision, shopSaleStatus: "hold" });
        } else {
          const privateRef = doc(collection(firebaseStore, PROCESSED_STOCK_COLLECTION));
          const publicData = publicProcessedStockTrace(data, product.firebaseId, product.isPublic);
          const batch = writeBatch(firebaseStore);
          batch.set(privateRef, { ...data, reservedQuantity: 0 });
          if (publicData) batch.set(doc(firebaseStore, PROCESSED_PUBLIC_STOCK_COLLECTION, privateRef.id), publicData);
          await batch.commit();
          upsertStock({ ...data, firebaseId: privateRef.id });
        }
        completed.add(row.id);
        setMultiRows((rows) => rows.filter((item) => item.id !== row.id));
      }
      };
      if (shopStockApiConfigured()) { await multiMutation.submit(saveRows); return; }
      await saveRows();
      clearFilters();
      setMultiOpen(false);
    } catch (error) {
      console.error("Error saving multiple processed stock entries:", error);
      setMultiError(stockApiError(error, "Alla poster kunde inte skapas. Redan sparade rader har tagits bort från listan; kontrollera behörighet och försök igen med återstående."));
    } finally {
      setMultiDialogLoading(false);
    }
  };

  const hasLoadError = Object.values(loadErrors).some(Boolean);
  const searchedEntries = getSortedEntries().filter(matchesSearch).filter((entry) => languageFilter === "all" ||
    (languageFilter === "unset" ? !isPackagingLanguage(entry.packagingLanguage) : entry.packagingLanguage === languageFilter));
  const visibleEntries = searchedEntries.filter((entry) => matchesFilter(entry, stockFilter));
  const hasFilters = !!searchTerm || stockFilter !== "all" || languageFilter !== "all";
  const currentEditingEntry = stockEntries.find((entry) => entry.firebaseId === editingEntry?.firebaseId) || editingEntry;
  const currentDeletingEntry = stockEntries.find((entry) => entry.firebaseId === deletingEntry?.firebaseId) || deletingEntry;
  const reservedMinimum = currentEditingEntry ? reservedOf(currentEditingEntry) : 0;
  const singleSourcesAvailable = !!singleProduct && !!singlePackage && singleSourceBatches.length > 0 &&
    singleSourceBatches.every((source) => getAvailablePackages(singleProduct.id, source.firebaseId, editingEntry?.firebaseId)
      .some((pkg) => pkg.id === singlePackage.id));
  const singlePackages = singleProduct ? getCommonAvailablePackages(singleProduct.id, singleSourceBatches, editingEntry?.firebaseId) : [];
  const quantityValid = Number.isInteger(singleQuantity) && singleQuantity >= Math.max(1, reservedMinimum);
  const singleBoxes = singlePackage ? getBoxPackageInfo(singlePackage) : null;
  const retryLoad = () => {
    setLoadErrors({});
    setLoadingCollections({ products: true, packages: true, batches: true, stock: true });
    setReload((value) => value + 1);
  };

  return (
    <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace StockWorkspace">
      <div className="FreshTabToolbar">
        <div><p className="FreshEyebrow">Lager</p><h2 className="FreshTabTitle">Processat lager</h2><p className="FreshTabSummary">Färdigpackade frystorkade och torkade produkter, kopplade till processbatch och förpackning.</p></div>
        <div className="StockPrimaryActions">
          <IconButton title="Om Processat lager" aria-label="Om Processat lager" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton>
          <Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={firebaseLoading || hasLoadError}>Ny lagerpost</Button>
        </div>
      </div>

      {hasLoadError && <Alert severity="error" className="StockLoadError" action={<Button color="inherit" onClick={retryLoad}>Försök igen</Button>}>{Object.values(loadErrors).filter(Boolean).join(" ")}</Alert>}
      <section className="StockOverview" aria-label="Översikt för hela lagret">
        {[
          { label: "Paket totalt", value: stockEntries.reduce((sum, entry) => sum + quantityOf(entry), 0), hint: "Hela lagret", Icon: Inventory2OutlinedIcon, className: "" },
          { label: "Reserverade", value: stockEntries.reduce((sum, entry) => sum + reservedOf(entry), 0), hint: "Registrerade reservationer", Icon: LockOutlinedIcon, className: "StockOverviewReserved" },
          { label: "Lediga paket", value: stockEntries.reduce((sum, entry) => sum + availableOf(entry), 0), hint: "Totalt minus reserverat per post", Icon: CheckCircleOutlineIcon, className: "StockOverviewAvailable" },
        ].map(({ label, value, hint, Icon, className }) => <div className={`StockOverviewItem ${className}`} key={label}><div><span>{label}</span><strong>{firebaseLoading || hasLoadError ? "—" : value.toLocaleString("sv-SE")}</strong><small>{hint}</small></div><Icon /></div>)}
      </section>

      <div className="StockSecondaryActions"><div className="ProcessedStockActions"><Button color="inherit" variant="outlined" startIcon={<PlaylistAddIcon />} onClick={handleOpenMultiAdd} disabled={firebaseLoading || hasLoadError}>Flera lagerposter</Button><Button color="inherit" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => setLabelsOpen(true)} disabled={firebaseLoading || hasLoadError}>Batch-etiketter PDF</Button></div><span>{firebaseLoading ? "Hämtar lageruppgifter…" : hasLoadError ? "Lageruppgifterna kunde inte hämtas" : `${stockCountLabel(stockEntries.length)} registrerade`}</span></div>

      <div className="BatchTools" role="search" aria-label="Filtrera processat lager">
        <div className="BatchSearchRow">
          <TextField className="BatchSearch" size="small" label="Sök i lagret" placeholder="Produkt, förpackning, datum, intern batch…" value={search} onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: search ? <InputAdornment position="end"><IconButton size="small" aria-label="Rensa sökning" onClick={() => setSearch("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : undefined }} />
          <TextField className="BatchSort" size="small" select label="Förpackningsspråk" value={languageFilter} onChange={(event) => setLanguageFilter(event.target.value as typeof languageFilter)}>
            <MenuItem value="all">Alla språk</MenuItem>{PACKAGING_LANGUAGES.map((language) => <MenuItem key={language.value} value={language.value}>{language.label}</MenuItem>)}<MenuItem value="unset">Ej angivet</MenuItem>
          </TextField>
          <TextField className="BatchSort" size="small" select label="Sortera" value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)}>
            <MenuItem value="created">Senast skapad</MenuItem><MenuItem value="package">Förpackning A–Ö</MenuItem><MenuItem value="batch">Packdatum, senast först</MenuItem>
          </TextField>
        </div>
        <div className="BatchFilterRow"><div className="BatchFilters" role="group" aria-label="Filtrera lagerposter">
          {STOCK_FILTERS.map((filter) => <button type="button" key={filter.value} className={`BatchFilter StockFilter-${filter.value}`} aria-pressed={stockFilter === filter.value} onClick={() => setStockFilter(filter.value)}>{filter.label}<span className="BatchFilterCount">{firebaseLoading || hasLoadError ? "—" : searchedEntries.filter((entry) => matchesFilter(entry, filter.value)).length}</span></button>)}
        </div>{hasFilters && <Button color="inherit" size="small" onClick={clearFilters}>Rensa filter</Button>}</div>
        <p className="BatchScope">Packat denna vecka: {moment().startOf("isoWeek").format("YYYY-MM-DD")}–{moment().endOf("isoWeek").format("YYYY-MM-DD")}. Filtren visar lagerposter; översikten gäller hela lagret.</p>
      </div>

      {firebaseLoading ? <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar lager och batchuppgifter…</p></div> : !hasLoadError && (
        <section className="BatchProcess StockList" aria-labelledby="stock-list-title">
          <div className="StockListHeader"><h3 id="stock-list-title">{STOCK_FILTERS.find((filter) => filter.value === stockFilter)?.label === "Alla" ? "Alla lagerposter" : STOCK_FILTERS.find((filter) => filter.value === stockFilter)?.label}</h3><span role="status">{visibleEntries.length} av {stockCountLabel(stockEntries.length)}</span></div>
          {visibleEntries.length ? <div className="BatchGrid StockCardGrid">{visibleEntries.map((entry) => {
            const pkg = getPackageById(entry.packageId);
            const product = getProductById(getEntryProductId(entry));
            const sources = getEntrySources(entry);
            const available = availableOf(entry);
            const reserved = reservedOf(entry);
            const packageType = pkg && PACKAGE_TYPES.find((option) => option.value === pkg.packageType)?.label;
            const productForm = pkg && PRODUCT_FORMS.find((option) => option.value === pkg.productForm)?.label;
            const boxInfo = pkg && getBoxPackageInfo(pkg);
            return <article className="BatchCard StockCard" key={entry.firebaseId} aria-label={`${getPackageLabel(entry.packageId, entry.productId)}, ${entry.batchId}, ${packagingLanguageLabel(entry.packagingLanguage)}`}>
              <div className="BatchCardHeader">
                <div className="BatchProductImage">{product?.icon || product?.mainImage ? <img src={product.icon || product.mainImage} alt="" loading="lazy" /> : <Inventory2OutlinedIcon />}</div>
                <div className="BatchIdentity"><h4>{product?.name_sv || "Okänd produkt"}</h4><span className="StockPackageSize">{pkg ? `${processedPackageLabelWithParent(pkg, packages)} · ${packageType} · ${productForm}` : "Förpackning saknas"}</span></div>
                <IconButton color="inherit" title="Redigera lagerpost" aria-label={`Redigera ${entry.batchId}, ${getPackageLabel(entry.packageId, entry.productId)}, ${packagingLanguageLabel(entry.packagingLanguage)}`} onClick={() => handleOpenEdit(entry)}><EditIcon fontSize="small" /></IconButton>
              </div>
              <div className="StockCardBatch"><span className="BatchId">{entry.packingDate ? "Datumkod" : "Äldre batch"} {entry.batchId}</span><span className="ProcessedStockLanguageBadge">Förpackning: {packagingLanguageLabel(entry.packagingLanguage)}</span>{isThisWeek(entry) && <span className="StockWeekBadge">Packat i veckan</span>}</div>
              <dl className="StockQuantities"><div><dt>Totalt</dt><dd>{quantityOf(entry).toLocaleString("sv-SE")}</dd></div><div><dt>Reserverat</dt><dd>{reserved.toLocaleString("sv-SE")}</dd></div><div className={available > 0 ? "StockAvailable" : "StockUnavailable"}><dt>Ledigt</dt><dd>{available.toLocaleString("sv-SE")}<small>paket</small></dd></div></dl>
              {!pkg && <p className="StockNotice">Förpackning saknas. Redigera posten för att koppla den till Processade förpackningar.</p>}
              {reserved > quantityOf(entry) && <p className="StockNotice">Reservationerna överstiger antalet paket. Kontrollera lagerposten.</p>}
              <dl className="BatchDates"><div><dt>Process</dt><dd>{entry.processDate || "Ej angivet"}</dd></div><div><dt>Packat</dt><dd>{entry.packingDate || "Ej angivet"}</dd></div></dl>
              <div className="StockOrigin"><span>Interna processbatcher</span><strong>{sources.length ? sources.map((source) => source.batchId).join(", ") : entry.sourceBatchIds?.join(", ") || entry.sourceBatchId || "Batchuppgift saknas"}</strong></div>
              {(entry.processedBy || entry.packedBy || entry.bestBeforeDate) && <p className="ProcessedStockTraceSummary">{entry.processedBy ? `Processad av ${entry.processedBy}` : ""}{entry.packedBy ? ` · Packad av ${entry.packedBy}` : ""}{entry.bestBeforeDate ? ` · Bäst före ${entry.bestBeforeDate}` : ""}</p>}
              <div className="BatchCardFooter"><span className="StockBoxSummary">{boxInfo ? `Motsvarar ${Math.floor(quantityOf(entry) / boxInfo.multiplier)} hela boxar à ${boxInfo.multiplier} paket` : reserved > 0 ? "Paket reserverade" : "Ingen kopplad box"}</span><IconButton color="inherit" className="BatchDelete" title={reserved > 0 ? "Visa varför lagerposten inte kan tas bort" : "Ta bort lagerpost"} aria-label={`Ta bort ${entry.batchId}, ${getPackageLabel(entry.packageId, entry.productId)}`} onClick={() => { if (deleteBusy || deleteMutation.pending) return; setDeletingEntry(entry); setDeleteError(""); setDeleteReason(""); deleteMutation.reset(); setDeleteOpen(true); }}>{reserved > 0 ? <LockOutlinedIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}</IconButton></div>
            </article>;
          })}</div> : <div className="BatchEmpty" role="status"><Inventory2OutlinedIcon /><strong>{stockEntries.length ? "Inga lagerposter matchar ditt urval" : "Inga lagerposter ännu"}</strong><p>{stockEntries.length ? "Prova en annan sökning eller rensa filtren." : "Registrera en packad lagerpost med totalantal och interna processbatcher för att komma igång."}</p>{hasFilters ? <Button color="inherit" onClick={clearFilters}>Rensa filter</Button> : <Button color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>Ny lagerpost</Button>}</div>}
        </section>
      )}

      {labelsOpen && <ProcessedStockLabelsDialog stock={stockEntries} packages={packages} products={parents} dataReady={!firebaseLoading && !hasLoadError} onClose={() => setLabelsOpen(false)} />}
      <StockDialog id="stock-edit" open={open} onClose={handleClose} title={editingEntry ? "Redigera lagerpost" : "Ny lagerpost"}
        description={editingEntry ? `${getPackageLabel(editingEntry.packageId, editingEntry.productId)} · ${editingEntry.batchId}` : "En lagerpost med ett totalt paketantal och interna kopplingar till en eller flera processbatcher."}
        busy={singleBusy || singleMutation.pending} error={singleMutation.error || singleError} wide
        actions={<><Button color="inherit" onClick={handleClose} disabled={singleBusy || singleMutation.pending}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveSingle}
          disabled={singleBusy || singleMutation.conflict || (!singleMutation.pending && (!singleProduct || !singlePackage || !singleSourcesAvailable || !quantityValid || !traceReady(singleTrace) || (!!editingEntry && shopStockApiConfigured() && !singleReason.trim())))}>{singleBusy ? "Sparar…" : singleMutation.pending ? "Bekräfta samma sparande" : "Spara lagerpost"}</Button></>}>
        <div className="StockForm">
          {!editingEntry && <StockFormSection title="Utgå från en lagerpost">
            <Autocomplete options={stockTemplates} value={selectedStockTemplate}
              getOptionLabel={stockTemplateLabel} isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId}
              onChange={(_event, entry) => applyStockTemplate(entry)} disabled={singleLocked || firebaseLoading || hasLoadError}
              noOptionsText="Inga lagerposter med aktiva förpackningar och giltiga, avslutade processbatcher kan användas som mall."
              renderOption={(props, entry) => <li {...props} key={entry.firebaseId}><div className="StockBatchOption"><strong>{getPackageLabel(entry.packageId, entry.productId)}</strong><span>Batch {entry.batchId} · Packat {entry.packingDate || "ej angivet"} · {packagingLanguageLabel(entry.packagingLanguage)}</span></div></li>}
              renderInput={(params) => <TextField {...params} label="Använd lagerpost som mall" size="small" fullWidth
                helperText="Valfritt. Återanvänd batchuppgifter och ursprung, välj förpackningsspråk och ange ett nytt antal paket." />} />
            {selectedStockTemplate && <Alert severity="info" className="ProcessedStockTemplateNotice">Uppgifter från batch {selectedStockTemplate.batchId} har fyllts i. Kontrollera språk, datum och antal för den nya lagerposten.</Alert>}
            <Button color="inherit" size="small" className="ProcessedStockTemplateReset" onClick={resetSingleForm} disabled={singleLocked}>Börja med tomt formulär</Button>
          </StockFormSection>}
          {editingEntry && !getPackageById(editingEntry.packageId) && <Alert severity="warning">Denna äldre lagerpost saknar en giltig förpackningslänk. Välj rätt förpackning innan du sparar.</Alert>}
          {reservedMinimum > 0 && <Alert severity="info">{reservedMinimum} paket är reserverade. Totalantalet måste vara minst {reservedMinimum} paket.</Alert>}
          <StockFormSection title="Produkt och intern batchkoppling">
            <div className="StockFormFields">
              <Autocomplete options={parents} getOptionLabel={(product) => `${product.name_sv}${product.name_en ? ` / ${product.name_en}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id} value={singleProduct}
                onChange={(_event, value) => { setSingleProduct(value); setSingleSourceBatches([]); setSinglePackage(null); }} disabled={singleLocked} noOptionsText="Inga produkter hittades"
                renderInput={(params) => <TextField {...params} label="Processad produkt *" size="small" fullWidth />} />
              <Autocomplete multiple options={singleProduct ? getSourceBatchesForProduct(singleProduct.id,
                editingEntry && getEntryProductId(editingEntry) === singleProduct.id ? sourceRefsOf(editingEntry) : []) : []}
                getOptionLabel={sourceBatchLabel} isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId}
                value={singleSourceBatches}
                onChange={(_event, value) => { setSingleSourceBatches([...value]); setSinglePackage(null); }}
                renderTags={(values, getTagProps) => values.map((batch, index) => <Chip {...getTagProps({ index })} key={batch.firebaseId} label={batch.batchId} size="small" />)}
                renderOption={(props, batch) => <li {...props} key={batch.firebaseId}><div className="StockBatchOption"><strong>{batch.batchId}</strong><span>{getSourceProductName(batch)} · {batch.freezeDryEnd ? `Avslutad ${moment(batch.freezeDryEnd).format("YYYY-MM-DD")}` : "Slutdatum ej angivet"}</span></div></li>}
                disabled={singleLocked || !singleProduct} noOptionsText="Inga avslutade batcher för produkten"
                renderInput={(params) => <TextField {...params} label="Processbatcher · internt *" size="small" fullWidth
                  helperText={!singleProduct ? "Välj produkt först." : "En eller flera avslutade batcher kan ingå i samma packade lagerpost. Referenserna publiceras aldrig."} />} />
            </div>
          </StockFormSection>
          <StockFormSection title="Förpackning och totalt antal">
            <div className="StockFormGrid">
              <Autocomplete options={singlePackages} getOptionLabel={getPackageOptionLabel} isOptionEqualToValue={(a, b) => a.id === b.id} value={singlePackage} onChange={(_event, value) => setSinglePackage(value)}
                disabled={singleLocked || !singleSourceBatches.length} noOptionsText="Ingen ledig förpackning för alla valda batcher"
                renderInput={(params) => <TextField {...params} label="Förpackning *" size="small" fullWidth helperText={!singleSourceBatches.length ? "Välj minst en intern processbatch först." : "Boxar räknas automatiskt från totalt antal paket."} />} />
              <TextField label="Totalt antal paket *" type="number" size="small" fullWidth value={singleQuantity} onChange={(event) => setSingleQuantity(event.target.value ? Number(event.target.value) : 0)} disabled={singleLocked || !singlePackage}
                inputProps={{ min: Math.max(1, reservedMinimum), step: 1 }} error={!!singlePackage && !quantityValid} helperText={singlePackage && !quantityValid ? `Ange ett heltal på minst ${Math.max(1, reservedMinimum)}.` : "Antalet gäller hela lagerposten, inte varje processbatch."} />
            </div>
          </StockFormSection>
          <StockFormSection title="Process, packning och spårning">
            <p className="StockHelpText ProcessedStockPrivacyHint">Datum och visningsnamn blir tillgängliga för kundspårning när produkten är publik. Interna batcher, antal paket, lagerplats och anteckningar stannar i den interna lagerposten.</p>
            <StockTraceFields value={singleTrace} onChange={(changes) => setSingleTrace((current) => ({ ...current, ...changes }))} busy={singleBusy || singleMutation.pending} />
          </StockFormSection>
          <StockLabelFields value={singleTrace} onChange={(changes) => setSingleTrace((current) => ({ ...current, ...changes }))} busy={singleLocked} />
          {editingEntry && shopStockApiConfigured() && <TextField label="Orsak till ändring *" size="small" fullWidth value={singleReason}
            onChange={(event) => setSingleReason(event.target.value)} inputProps={{ maxLength: 500 }} disabled={singleLocked}
            helperText="Sparas i lagerhistoriken och används vid konfliktkontroll." />}
          {singlePackage && singleSourceBatches.length > 0 && <section className="StockSavePreview" aria-label="Sammanfattning av lagerpost">
            <div className="StockPreviewHeading"><strong>{editingEntry ? "Efter ändringen" : "Det här registreras"}</strong><span>1 lagerpost</span></div>
            <div className="StockPreviewRow"><div><strong>{singleSourceBatches.length} interna processbatcher</strong><span>{singleSourceBatches.map((source) => source.batchId).join(", ")}</span></div><span>{singleProduct?.isPublic ? "Publik spårning" : "Endast internt"}</span></div>
            <div className="StockPreviewTotal"><span>{singleQuantity} paket totalt{editingEntry && reservedMinimum ? ` · ${Math.max(0, singleQuantity - reservedMinimum)} lediga` : ""}</span>{singleBoxes && quantityValid && <small>{Math.floor(singleQuantity / singleBoxes.multiplier)} hela boxar à {singleBoxes.multiplier} paket totalt</small>}</div>
          </section>}
        </div>
      </StockDialog>

      <StockDialog id="stock-multi" open={multiOpen} onClose={handleCloseMulti} title="Skapa flera lagerposter"
        description="Varje rad är en egen packad lagerpost med ett totalt paketantal och en intern processbatch."
        busy={multiBusy || multiMutation.pending} error={multiMutation.error || multiError} wide
        actions={<><Button color="inherit" onClick={handleCloseMulti} disabled={multiBusy || multiMutation.pending}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveMulti} disabled={multiBusy || multiMutation.conflict || (!multiMutation.pending && (!multiRows.length || multiRows.some((row) => !row.product || !row.sourceBatch || !row.package || !Number.isInteger(row.quantity) || row.quantity < 1 || !traceReady(row.trace))))}>{multiBusy ? "Skapar lagerposter…" : multiMutation.pending ? "Bekräfta samma lagerposter" : `Skapa ${stockCountLabel(multiRows.length)}`}</Button></>}>
        <div className="StockForm">
          <div className="StockMultiSummary" role="status"><strong>{stockCountLabel(multiRows.length)}</strong><span>{multiRows.reduce((sum, row) => sum + (row.package && row.quantity > 0 ? row.quantity : 0), 0)} paket valda</span></div>
          {multiRows.map((row, index) => {
            const options = getMultiRowPackages(row);
            const boxInfo = row.package ? getBoxPackageInfo(row.package) : null;
            return <section className="StockMultiRow" key={row.id} aria-label={`Lagerpost ${index + 1}`}>
              <div className="StockMultiRowHeader"><h3>Lagerpost {index + 1}</h3><IconButton color="inherit" aria-label={`Ta bort rad ${index + 1}`} title="Ta bort rad" onClick={() => setMultiRows((rows) => rows.filter((item) => item.id !== row.id))} disabled={multiLocked || multiRows.length === 1}><CloseIcon fontSize="small" /></IconButton></div>
              <div className="StockFormGrid">
                <Autocomplete options={parents} getOptionLabel={(product) => product.name_sv} isOptionEqualToValue={(a, b) => a.id === b.id} value={row.product}
                  onChange={(_event, value) => updateMultiRow(row.id, { product: value, sourceBatch: null, package: null })} disabled={multiLocked} noOptionsText="Inga produkter hittades"
                  renderInput={(params) => <TextField {...params} label="Produkt *" size="small" fullWidth />} />
                <Autocomplete options={row.product ? getSourceBatchesForProduct(row.product.id) : []} getOptionLabel={sourceBatchLabel} isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId} value={row.sourceBatch}
                  onChange={(_event, value) => updateMultiRow(row.id, { sourceBatch: value, package: null })} disabled={multiLocked || !row.product} noOptionsText="Inga valbara batcher för produkten"
                  renderInput={(params) => <TextField {...params} label="Processbatch · internt *" size="small" fullWidth helperText={!row.product ? "Välj produkt först." : "Referensen publiceras inte."} />} />
                <Autocomplete options={options} getOptionLabel={getPackageOptionLabel} isOptionEqualToValue={(a, b) => a.id === b.id} value={row.package}
                  onChange={(_event, value) => updateMultiRow(row.id, { package: value })} disabled={multiLocked || !row.sourceBatch} noOptionsText="Ingen ledig förpackning för den här batchen"
                  renderInput={(params) => <TextField {...params} label="Förpackning *" size="small" fullWidth helperText={!row.sourceBatch ? "Välj batch först." : !options.length ? "Ingen aktiv förpackning är valbar för batchen." : undefined} />} />
                <TextField label="Totalt antal paket *" type="number" size="small" fullWidth value={row.quantity} onChange={(event) => updateMultiRow(row.id, { quantity: event.target.value ? Number(event.target.value) : 0 })}
                  disabled={multiLocked || !row.package} inputProps={{ min: 1, step: 1 }} error={!!row.package && (!Number.isInteger(row.quantity) || row.quantity < 1)} helperText={row.package && (!Number.isInteger(row.quantity) || row.quantity < 1) ? "Ange ett heltal på minst 1." : undefined} />
              </div>
              <h4 className="ProcessedStockMultiTraceTitle">Process och packning</h4>
              <StockTraceFields value={row.trace} onChange={(changes) => updateMultiRow(row.id, { trace: { ...row.trace, ...changes } })} busy={multiBusy || multiMutation.pending} />
              <StockLabelFields value={row.trace} onChange={(changes) => updateMultiRow(row.id, { trace: { ...row.trace, ...changes } })} busy={multiLocked} />
              {row.product && <p className="StockHelpText">{row.product.isPublic ? "En separat publik spårningspost skapas." : "Lagerposten blir endast intern."}</p>}
              {boxInfo && row.quantity > 0 && <p className="StockHelpText">Motsvarar {Math.floor(row.quantity / boxInfo.multiplier)} hela boxar à {boxInfo.multiplier} paket.</p>}
            </section>;
          })}
          <Button className="StockAddRow" color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={() => setMultiRows((rows) => [...rows, createMultiRow()])} disabled={multiLocked}>Lägg till en rad</Button>
        </div>
      </StockDialog>

      <StockDialog id="stock-delete" open={deleteOpen} onClose={() => setDeleteOpen(false)} title={currentDeletingEntry && reservedOf(currentDeletingEntry) > 0 ? "Lagerposten har reservationer" : "Ta bort lagerpost?"}
        description={currentDeletingEntry && reservedOf(currentDeletingEntry) > 0 ? "Frigör reservationerna innan du tar bort lagerposten." : "Intern lagerpost och eventuell publik spårning tas bort. Processbatcher och förpackning behålls."}
        busy={deleteBusy || deleteMutation.pending} error={deleteMutation.error || deleteError}
        actions={<><Button color="inherit" onClick={() => setDeleteOpen(false)} disabled={deleteBusy || deleteMutation.pending}>Stäng</Button><Button className="StockDeleteButton" color="inherit" variant="contained" onClick={handleDeleteEntry} disabled={deleteBusy || deleteMutation.conflict || (!deleteMutation.pending && (!currentDeletingEntry || reservedOf(currentDeletingEntry) > 0 || (shopStockApiConfigured() && !deleteReason.trim())))}>{deleteBusy ? "Tar bort…" : deleteMutation.pending ? "Bekräfta samma borttagning" : "Ta bort lagerpost"}</Button></>}>
        {currentDeletingEntry && <div className="StockDeleteSummary"><Inventory2OutlinedIcon /><div><strong>{getPackageLabel(currentDeletingEntry.packageId, currentDeletingEntry.productId)}</strong><span>{currentDeletingEntry.batchId}</span><small>{quantityOf(currentDeletingEntry)} paket · {reservedOf(currentDeletingEntry)} reserverade</small></div></div>}
        {shopStockApiConfigured() && <TextField label="Orsak till borttagning *" size="small" fullWidth value={deleteReason}
          onChange={(event) => setDeleteReason(event.target.value)} inputProps={{ maxLength: 500 }} disabled={deleteLocked}
          helperText="Godkända eller återkallade lagerposter måste behållas för spårbarhet." />}
      </StockDialog>

      <StockDialog id="stock-info" open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} title="Om Processat lager" description="Lagerposter knyter ihop processade förpackningar, avslutade processbatcher och antal paket."
        actions={<Button color="inherit" variant="contained" onClick={() => setInfoDialogOpen(false)}>Stäng</Button>}>
        <div className="StockForm">
          <StockFormSection title="Registrera processat lager"><ol className="StockHelpSteps"><li><strong>Välj produkt och interna batcher</strong><span>En lagerpost kan länka flera avslutade processbatcher. Referenserna används bara för intern spårning.</span></li><li><strong>Välj förpackning och totalantal</strong><span>Antalet gäller hela lagerposten, även om flera processbatcher ingår. Aktiva påsar och burkar från Processade förpackningar visas.</span></li><li><strong>Fyll i process och packning</strong><span>Datum, bäst före och angivna visningsnamn kan användas för kundspårning. Lagerplats, anteckningar, antal och batchreferenser stannar internt.</span></li></ol></StockFormSection>
          <StockFormSection title="Läs av lager och reservationer"><dl className="StockHelpDefinitions"><div><dt>Totalt</dt><dd>Alla registrerade paket, inklusive reserverade.</dd></div><div><dt>Reserverat</dt><dd>Paket med registrerad reservation på lagerposten.</dd></div><div><dt>Ledigt</dt><dd>Antalet paket som återstår efter reservationer, lägst noll per post.</dd></div><div><dt>Boxar</dt><dd>Beräknas automatiskt från totalt antal paket när en matchande boxförpackning finns. Boxarna är inte extra lager.</dd></div></dl></StockFormSection>
          <Alert severity="info">Packat i veckan filtrerar på packdatum. En separat publik spårningspost skapas bara när produkten är markerad som publik. Äldre lagerposter finns kvar internt och kan kompletteras via Redigera.</Alert>
        </div>
      </StockDialog>
    </div>
  );
}

export default DashBoardFreezeDryStock;
